import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../lib/toast';
import Icon from '../../components/dashboard/DashboardIcons';
import { useParentData } from '../../context/ParentDataContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_LABELS = { pending: 'قيد المراجعة', approved: 'مُعتمد', rejected: 'مرفوض', unlinked: 'تم فك الربط' };

export default function ParentAccountPage() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { refreshUnreadCount } = useParentData() || {};
  const [children, setChildren] = useState([]);
  const [requests, setRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [confirmUnlink, setConfirmUnlink] = useState(null);

  const load = useCallback(async () => {
    try {
      const [childrenRes, requestsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/parent/me/children`, auth()),
        axios.get(`${API}/parent/me/link-requests`, auth()),
        axios.get(`${API}/parent/me/notifications`, auth()),
      ]);
      setChildren(childrenRes.data || []);
      setRequests(requestsRes.data || []);
      setNotifications(notificationsRes.data || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/parent/me/notifications/read-all`, {}, auth());
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      refreshUnreadCount && refreshUnreadCount();
    } catch { /* noop */ }
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/parent/me/notifications/${id}/read`, {}, auth());
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      refreshUnreadCount && refreshUnreadCount();
    } catch { /* noop */ }
  };

  const unlink = async (linkId) => {
    try {
      await axios.post(`${API}/parent/me/unlink/${linkId}`, {}, auth());
      toast.success('تم فك الربط بنجاح');
      setConfirmUnlink(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل فك الربط');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.replace('/login');
  };

  return (
    <div className="space-y-6" data-testid="parent-account-page">
      <h1 className="text-xl font-black text-[#0A192F]">حسابي</h1>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#0A192F] text-lg font-bold text-white">{user.full_name?.charAt(0) || 'و'}</div>
          <div>
            <div className="font-bold text-slate-900" data-testid="parent-account-name">{user.full_name}</div>
            <div dir="ltr" className="text-sm text-slate-500" data-testid="parent-account-phone">{user.username}</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 text-sm font-bold text-slate-700">أبنائي المرتبطون</h3>
        {children.length === 0 ? (
          <p className="text-xs text-slate-400">لا يوجد أبناء مرتبطون بحسابك حالياً</p>
        ) : (
          <div className="space-y-2" data-testid="parent-account-children-list">
            {children.map((c) => (
              <div key={c.player_id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5">
                <div>
                  <div className="text-sm font-bold text-slate-800">{c.player_name}</div>
                  <div className="text-xs text-slate-500">{c.club_name}</div>
                </div>
                <button
                  onClick={() => setConfirmUnlink(c)}
                  data-testid={`parent-unlink-btn-${c.player_id}`}
                  className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50"
                >
                  هذا ليس ابني
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-bold text-slate-700"><Icon name="bell" className="h-4 w-4 text-teal-500" /> الإشعارات</h3>
          {notifications.some((n) => !n.is_read) && (
            <button onClick={markAllRead} data-testid="parent-mark-all-notifications-read" className="text-xs font-bold text-teal-600 hover:text-teal-700">تعليم الكل كمقروء</button>
          )}
        </div>
        {notifications.length === 0 ? (
          <p className="text-xs text-slate-400">لا توجد إشعارات حالياً</p>
        ) : (
          <div className="max-h-80 space-y-2 overflow-y-auto" data-testid="parent-notifications-list">
            {notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => !n.is_read && markRead(n.id)}
                data-testid={`parent-notification-${n.id}`}
                className={`block w-full rounded-xl px-3 py-2.5 text-right transition-colors ${n.is_read ? 'bg-slate-50' : 'bg-teal-50/70 hover:bg-teal-50'}`}
              >
                <div className="flex items-center gap-1.5">
                  {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />}
                  <span className="text-sm font-bold text-slate-800">{n.title}</span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{n.content}</p>
                <p className="mt-1 text-[10px] text-slate-400">{new Date(n.created_at).toLocaleDateString('ar-SA')}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 text-sm font-bold text-slate-700">طلبات الربط</h3>
        {requests.length === 0 ? (
          <p className="text-xs text-slate-400">لا توجد طلبات ربط سابقة</p>
        ) : (
          <div className="space-y-2" data-testid="parent-account-requests-list">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2.5 text-xs">
                <span className="text-slate-600">{r.club_name}</span>
                <span className={`rounded-full px-2 py-0.5 font-bold ${r.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>{STATUS_LABELS[r.status] || r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={handleLogout} data-testid="parent-account-logout-btn" className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 py-3 text-sm font-bold text-red-600 hover:bg-red-100">
        <Icon name="logout" className="h-4 w-4" /> تسجيل الخروج
      </button>

      {confirmUnlink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="parent-unlink-confirm-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6">
            <h4 className="font-bold text-slate-900">تأكيد فك الربط</h4>
            <p className="mt-2 text-sm text-slate-500">سيتم فك ربط «{confirmUnlink.player_name}» بحسابك فوراً. هل أنت متأكد؟</p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirmUnlink(null)} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">إلغاء</button>
              <button onClick={() => unlink(confirmUnlink.link_id)} data-testid="parent-unlink-confirm-btn" className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-bold text-white">تأكيد فك الربط</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
